/**
 * GlobalCursor — replaces the native cursor site-wide with a small circle dot
 * (mix-blend-difference, so it stays visible on any background). Rendered once at
 * the app root. Over a clickable element the dot GROWS and emits small pulsing
 * semi-transparent waves. Hides over `PresenceCursor` zones (which show their own
 * cursor) and over text fields (where the native caret is kept).
 *
 * Over a BLUE surface (accent buttons, the gradient pricing card, …) the
 * difference-blended white reads reddish — so there the cursor switches to a
 * solid GOLD dot with normal blending instead.
 * Client-only + pointer-only: SSG renders an invisible dot; touch never shows it.
 */
import { useEffect, useRef, useState } from "react";

const HIDE_OVER = "[data-presence-zone], input, textarea, select, [contenteditable]";
const CLICKABLE = 'a, button, [role="button"], label, select, summary, [data-clickable]';

// Is an "r,g,b[,a]" colour blue-dominant (and not mostly transparent)?
function isBlue(rgb?: string | null) {
  if (!rgb) return false;
  const m = rgb.match(/\d+(\.\d+)?/g);
  if (!m || m.length < 3) return false;
  const [r, g, b, a] = m.map(Number);
  if (a !== undefined && a < 0.5) return false;
  return b > 90 && b > r + 25 && b > g + 10;
}

// Walk up from the element under the cursor: blue if a solid bg or a gradient
// stop is blue; stop at the first opaque non-blue bg (we're sitting on top of it).
function overBlue(start: Element | null) {
  let el: Element | null = start;
  for (let i = 0; el && i < 8 && el !== document.documentElement; i++) {
    const cs = getComputedStyle(el);
    if (isBlue(cs.backgroundColor)) return true;
    if (cs.backgroundImage && cs.backgroundImage !== "none") {
      const cols = cs.backgroundImage.match(/rgba?\([^)]*\)/g);
      if (cols && cols.some(isBlue)) return true;
    }
    const bc = cs.backgroundColor;
    if (bc && bc !== "transparent") {
      const m = bc.match(/\d+(\.\d+)?/g);
      const a = m && m.length >= 4 ? Number(m[3]) : 1;
      if (a >= 0.95) return false; // opaque, non-blue surface → not blue
    }
    el = el.parentElement;
  }
  return false;
}

export function GlobalCursor() {
  const wrap = useRef<HTMLDivElement>(null);
  const [shown, setShown] = useState(false);
  const [hot, setHot] = useState(false);
  const [blue, setBlue] = useState(false);
  const shownRef = useRef(false);
  const hotRef = useRef(false);
  const blueRef = useRef(false);

  useEffect(() => {
    document.body.classList.add("global-cursor");
    const onMove = (e: MouseEvent) => {
      const t = e.target as Element | null;
      const hide = !!t?.closest?.(HIDE_OVER);
      const clickable = !hide && !!t?.closest?.(CLICKABLE);
      const onBlue = !hide && overBlue(t);
      const w = wrap.current;
      if (w) w.style.transform = `translate(${e.clientX}px, ${e.clientY}px)`;
      if (shownRef.current === hide) { shownRef.current = !hide; setShown(!hide); }
      if (hotRef.current !== clickable) { hotRef.current = clickable; setHot(clickable); }
      if (blueRef.current !== onBlue) { blueRef.current = onBlue; setBlue(onBlue); }
    };
    const onLeave = () => { shownRef.current = false; setShown(false); };
    window.addEventListener("mousemove", onMove, { passive: true });
    document.addEventListener("mouseleave", onLeave);
    return () => {
      document.body.classList.remove("global-cursor");
      window.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseleave", onLeave);
    };
  }, []);

  // Over blue: solid gold, normal blend. Elsewhere: white, difference blend.
  const ring = blue ? "border-ds-gold" : "border-white";
  const fill = blue ? "bg-ds-gold" : "bg-white";

  return (
    <div
      ref={wrap}
      aria-hidden
      className={`pointer-events-none fixed left-0 top-0 z-[9999] transition-opacity duration-150 will-change-transform ${blue ? "" : "mix-blend-difference"} ${shown ? "opacity-100" : "opacity-0"}`}
    >
      {/* one pulsing wave — only over a clickable; emanates from the ring edge */}
      <span className={`absolute left-0 top-0 h-9 w-9 rounded-full border ${blue ? "border-ds-gold/70" : "border-white/70"} ${hot ? "ds-cursor-ripple" : "hidden"}`} />
      {/* hollow ring — appears over a clickable (no big white fill) */}
      <span className={`absolute left-0 top-0 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 ${ring} transition-all duration-200 ease-out ${hot ? "h-9 w-9 opacity-100" : "h-0 w-0 opacity-0"}`} />
      {/* center dot — always; shrinks to a point when the ring is shown */}
      <span className={`absolute left-0 top-0 -translate-x-1/2 -translate-y-1/2 rounded-full ${fill} transition-all duration-200 ease-out ${hot ? "h-1.5 w-1.5" : "h-3 w-3"}`} />
    </div>
  );
}
